import { Invoice } from '@principle-theorem/principle-core';
import {
  PatientCollection,
  type IBrand,
  type IInvoice,
  type IOrganisation,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  collectionGroupQuery,
  orderBy,
  snapshot,
  toTimestamp,
  where,
  type Query,
  type WithRef,
} from '@principle-theorem/shared';
import { BigQueryJobName } from '../../job-names';
import { IInvoiceWriteTopicData } from '../../topic-data/topic-data';
import {
  BasicBigQuerySyncHandler,
  IBigQuerySyncRequest,
} from '../big-query-sync';

export class BigQueryInvoicesSyncHandler extends BasicBigQuerySyncHandler<
  IInvoice,
  IInvoiceWriteTopicData
> {
  readonly bigQueryJobName = BigQueryJobName.InvoiceWrite;

  getQuery(request: IBigQuerySyncRequest): Query<IInvoice> {
    return collectionGroupQuery<IInvoice>(
      PatientCollection.Invoices,
      where('practice.ref', '==', request.practiceRef),
      where('createdAt', '>=', request.range.from),
      where('createdAt', '<=', request.range.to),
      orderBy('createdAt')
    );
  }

  async getTopicData(
    invoice: WithRef<IInvoice>
  ): Promise<IInvoiceWriteTopicData> {
    const patientRef = Firestore.getParentDocRef<IPatient>(invoice.ref);
    const brandRef = Firestore.getParentDocRef<IBrand>(patientRef);
    const organisationRef = Firestore.getParentDocRef<IOrganisation>(brandRef);

    return {
      organisation: await Firestore.getDoc(organisationRef),
      brand: await Firestore.getDoc(brandRef),
      practice: await Firestore.getDoc(invoice.practice.ref),
      patient: await Firestore.getDoc(patientRef),
      invoice,
      timestamp: toTimestamp(),
      transactions: await snapshot(Invoice.transactions$(invoice)),
      status: {
        before: invoice.status,
        after: invoice.status,
      },
    };
  }
}
